import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { Message, Contact, User } from '../types';
import { getStoredUser, clearAuthData } from '../utils/auth';
import './Dashboard.css';

// SVG Icons as components
const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M15.009 13.805h-.636l-.22-.219a5.184 5.184 0 0 0 1.256-3.386 5.207 5.207 0 1 0-5.207 5.208 5.183 5.183 0 0 0 3.385-1.255l.221.22v.635l4.004 3.999 1.194-1.195-3.997-4.007zm-4.808 0a3.605 3.605 0 1 1 0-7.21 3.605 3.605 0 0 1 0 7.21z"></path></svg>
);

const NewChatIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M19.005 3.175H4.674C3.642 3.175 3 3.789 3 4.821V21.02l3.544-3.514h12.461c1.033 0 2.064-1.06 2.064-2.093V4.821c-.001-1.032-1.032-1.646-2.064-1.646zm-4.989 9.869H7.041V11.1h6.975v1.944zm3-4H7.041V7.1h9.975v1.944z"></path></svg>
);

const MenuIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M12 7a2 2 0 1 0-.001-4.001A2 2 0 0 0 12 7zm0 2a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 9zm0 6a2 2 0 1 0-.001 3.999A2 2 0 0 0 12 15z"></path></svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M12 4l1.4 1.4L7.8 11H20v2H7.8l5.6 5.6L12 20l-8-8 8-8z"></path></svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M1.101 21.757 23.8 12.028 1.101 2.3l.011 7.912 13.623 1.816-13.623 1.817-.011 7.912z"></path></svg>
);

const PersonAddIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path></svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"></path></svg>
);

const SingleTickIcon = () => (
  <svg viewBox="0 0 16 11" width="16" height="11"><path fill="currentColor" d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.095a.434.434 0 0 0-.329-.156.46.46 0 0 0-.336.135.46.46 0 0 0 0 .672l2.357 2.526a.452.452 0 0 0 .312.148h.036a.505.505 0 0 0 .35-.161l6.543-8.076a.477.477 0 0 0-.047-.705z"></path></svg>
);

const DoubleTickIcon = () => (
  <svg viewBox="0 0 16 11" width="16" height="11"><path fill="currentColor" d="M11.071.653a.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636-2.011-2.095a.434.434 0 0 0-.329-.156.46.46 0 0 0-.336.135.46.46 0 0 0 0 .672l2.357 2.526a.452.452 0 0 0 .312.148h.036a.505.505 0 0 0 .35-.161l6.543-8.076a.477.477 0 0 0-.047-.705zM8.632 7.386l-.544.645 2.36 2.527a.452.452 0 0 0 .312.148h.036a.505.505 0 0 0 .35-.161l6.543-8.076a.477.477 0 0 0-.047-.705.457.457 0 0 0-.304-.102.493.493 0 0 0-.381.178l-6.19 7.636z" transform="translate(-1.5 0)"></path></svg>
);

const LockIcon = () => (
  <svg viewBox="0 0 10 12" width="10" height="12"><path fill="currentColor" d="M5.009 0C3.351 0 2.008 1.343 2.008 3.001V4.5H.506v6.5c0 .55.45 1 1.001 1h7.005c.55 0 1.001-.45 1.001-1V4.5H8.011V3.001C8.011 1.343 6.668 0 5.009 0zm0 1.5c.831 0 1.502.671 1.502 1.501V4.5H3.507V3.001c0-.83.671-1.501 1.502-1.501z"></path></svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path></svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24"><path fill="currentColor" d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path></svg>
);

const FailedIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16"><path fill="#EA4335" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path></svg>
);

const ChevronDownIcon = () => (
  <svg viewBox="0 0 18 18" width="18" height="18"><path fill="currentColor" d="M3.3 4.6L9 10.3l5.7-5.7 1.6 1.6-7.3 7.4-7.3-7.4z"></path></svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
);

// Helper: Conversation type grouping messages by phone
interface Conversation {
  phoneNumber: string;
  contact: Contact | null;
  lastMessage: Message | null;
  messages: Message[];
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Chat state
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatTotal, setChatTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState<'all' | 'unread' | 'contacts'>('all');

  // Panels
  const [showNewChat, setShowNewChat] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [newContactSearch, setNewContactSearch] = useState('');
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // Image viewer
  const [viewerImage, setViewerImage] = useState<{ url: string; filename: string } | null>(null);

  // Delete message
  const [messageMenuId, setMessageMenuId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ messageId: string; content: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk select mode
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // New contact form
  const [newContactForm, setNewContactForm] = useState({
    phoneNumber: '',
    displayName: '',
    firstName: '',
    lastName: '',
  });

  // Edit contact
  const [showEditContactModal, setShowEditContactModal] = useState(false);
  const [editContactForm, setEditContactForm] = useState({
    contactId: 0,
    phoneNumber: '',
    displayName: '',
    firstName: '',
    lastName: '',
    email: '',
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesAreaRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Scroll to bottom when chat messages change and loading is complete
  // Skip scroll when loading older messages (isLoadingMoreRef)
  useEffect(() => {
    if (isLoadingMoreRef.current) {
      isLoadingMoreRef.current = false;
      return;
    }
    if (!chatLoading && chatMessages.length > 0) {
      scrollToBottom();
      const timer1 = setTimeout(() => {
        scrollToBottom();
      }, 100);
      const timer2 = setTimeout(() => {
        scrollToBottom();
      }, 500);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [chatMessages, chatLoading]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
      // Close message context menu when clicking outside
      const target = e.target as HTMLElement;
      if (!target.closest('.wa-msg-menu-container')) {
        setMessageMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToBottom = () => {
    // Method 1: Use scrollTop on container (most reliable)
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
    // Method 2: Fallback to scrollIntoView
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto', block: 'end' });
    }
  };

  // Parse date treating SQLite IST timestamps correctly
  const parseDate = (dateString: string) => {
    if (!dateString) return new Date();
    // Timestamps are stored in IST (UTC+5:30) without timezone indicator
    // Append '+05:30' so JavaScript parses them as IST
    if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      return new Date(dateString.replace(' ', 'T') + '+05:30');
    }
    return new Date(dateString);
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const userData = getStoredUser();
      if (userData) {
        setUser({
          id: userData.userId || userData.id || 0,
          username: userData.username,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role,
          isActive: true,
          createdAt: ''
        });
      }

      // Load messages and contacts in parallel
      const [messagesResponse, contactsResponse] = await Promise.all([
        apiService.getMessages(1, 100),
        apiService.getContacts()
      ]);

      if (messagesResponse?.success && messagesResponse?.data) {
        const messageData = messagesResponse.data.messages && Array.isArray(messagesResponse.data.messages)
          ? messagesResponse.data.messages : [];
        setMessages(messageData);
      } else {
        setMessages([]);
      }

      if (contactsResponse?.success && contactsResponse?.data) {
        setContacts(Array.isArray(contactsResponse.data) ? contactsResponse.data : []);
      } else {
        setContacts([]);
      }
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Build conversation list from messages
  const getConversations = useCallback((): Conversation[] => {
    const phoneMap = new Map<string, Conversation>();

    // Group messages by phone number
    messages.forEach(msg => {
      const phone = msg.phoneNumber;
      if (!phoneMap.has(phone)) {
        const contact = contacts.find(c => c.phoneNumber === phone) || null;
        phoneMap.set(phone, {
          phoneNumber: phone,
          contact,
          lastMessage: msg,
          messages: [msg],
        });
      } else {
        const conv = phoneMap.get(phone)!;
        conv.messages.push(msg);
        // Keep the most recent message as lastMessage
        if (parseDate(msg.createdAt) > parseDate(conv.lastMessage!.createdAt)) {
          conv.lastMessage = msg;
        }
      }
    });

    // Sort: unread first, then by most recent message
    const convs = Array.from(phoneMap.values());
    convs.sort((a, b) => {
      // Unread (incoming + received status) on top
      const aUnread = a.lastMessage?.messageType === 'incoming' && a.lastMessage?.status === 'received' ? 1 : 0;
      const bUnread = b.lastMessage?.messageType === 'incoming' && b.lastMessage?.status === 'received' ? 1 : 0;
      if (aUnread !== bUnread) return bUnread - aUnread;
      // Then by most recent message
      if (!a.lastMessage && !b.lastMessage) return 0;
      if (!a.lastMessage) return 1;
      if (!b.lastMessage) return -1;
      return parseDate(b.lastMessage.createdAt).getTime() - parseDate(a.lastMessage.createdAt).getTime();
    });

    return convs;
  }, [messages, contacts]);

  // Filter conversations
  const filteredConversations = useCallback(() => {
    if (filterTab === 'contacts') {
      // Show all contacts (with or without messages), sorted alphabetically
      let allContacts = contacts.map(contact => {
        const existing = getConversations().find(c => c.phoneNumber === contact.phoneNumber);
        return existing || {
          phoneNumber: contact.phoneNumber,
          contact,
          lastMessage: null,
          messages: [],
        };
      });

      // Also include conversations for numbers not in contacts
      const contactPhones = new Set(contacts.map(c => c.phoneNumber));
      const extraConvs = getConversations().filter(c => !contactPhones.has(c.phoneNumber));
      allContacts = [...allContacts, ...extraConvs];

      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        allContacts = allContacts.filter(c => {
          const name = getConversationName(c).toLowerCase();
          const phone = c.phoneNumber.toLowerCase();
          return name.includes(q) || phone.includes(q);
        });
      }

      allContacts.sort((a, b) => getConversationName(a).localeCompare(getConversationName(b)));
      return allContacts;
    }

    // "all" and "unread" tabs - only show conversations that have messages
    let convs = getConversations();

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      convs = convs.filter(c => {
        const name = getConversationName(c).toLowerCase();
        const phone = c.phoneNumber.toLowerCase();
        const lastMsg = c.lastMessage?.messageContent?.toLowerCase() || '';
        return name.includes(q) || phone.includes(q) || lastMsg.includes(q);
      });
    }

    if (filterTab === 'unread') {
      convs = convs.filter(c => c.lastMessage?.messageType === 'incoming' && c.lastMessage?.status === 'received');
    }

    return convs;
  }, [getConversations, searchQuery, filterTab, contacts]);

  const MESSAGES_PER_PAGE = 100;

  // Select a chat
  const selectChat = async (phoneNumber: string) => {
    // Always reset and reload, even if same phone selected
    setChatMessages([]);
    setChatTotal(0);
    setSelectedPhone(phoneNumber);
    setMobileShowChat(true);
    setChatLoading(true);
    exitBulkSelect();

    try {
      const response = await apiService.getMessagesByPhone(phoneNumber, MESSAGES_PER_PAGE, 0);
      if (response?.success && response?.data) {
        const data = response.data as any;
        const msgs = Array.isArray(data.messages) ? data.messages : (Array.isArray(data) ? data : []);
        msgs.sort((a: Message, b: Message) => parseDate(a.createdAt).getTime() - parseDate(b.createdAt).getTime());
        setChatMessages(msgs);
        setChatTotal(data.total || msgs.length);
      } else {
        setChatMessages([]);
        setChatTotal(0);
      }
    } catch {
      setChatMessages([]);
      setChatTotal(0);
    } finally {
      setChatLoading(false);
    }
  };

  // Load older messages
  const loadMoreMessages = async () => {
    if (!selectedPhone || loadingMore) return;
    setLoadingMore(true);

    // Save scroll height before loading
    const scrollArea = messagesAreaRef.current;
    const prevScrollHeight = scrollArea?.scrollHeight || 0;

    try {
      const response = await apiService.getMessagesByPhone(selectedPhone, MESSAGES_PER_PAGE, chatMessages.length);
      if (response?.success && response?.data) {
        const data = response.data as any;
        const olderMsgs = Array.isArray(data.messages) ? data.messages : [];
        if (olderMsgs.length > 0) {
          // Merge older messages (they come DESC, reverse to ASC), deduplicate
          olderMsgs.sort((a: Message, b: Message) => parseDate(a.createdAt).getTime() - parseDate(b.createdAt).getTime());
          const existingIds = new Set(chatMessages.map(m => m.messageId));
          const newMsgs = olderMsgs.filter((m: Message) => !existingIds.has(m.messageId));

          // Prevent auto-scroll-to-bottom when older messages are prepended
          isLoadingMoreRef.current = true;

          setChatMessages(prev => [...newMsgs, ...prev]);
          setChatTotal(data.total || chatMessages.length + newMsgs.length);

          // Restore scroll position after DOM update so user stays where they were
          requestAnimationFrame(() => {
            if (scrollArea) {
              scrollArea.scrollTop = scrollArea.scrollHeight - prevScrollHeight;
            }
          });
        }
      }
    } catch {
      setError('Failed to load older messages');
    } finally {
      setLoadingMore(false);
    }
  };

  // Send message
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPhone || !newMessage.trim()) return;

    try {
      const response = await apiService.sendMessage(selectedPhone, newMessage.trim());
      if (response.success) {
        setNewMessage('');
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
        // Reload chat and conversations
        selectChat(selectedPhone);
        // Reload all messages for sidebar update
        const messagesResponse = await apiService.getMessages(1, 100);
        if (messagesResponse?.success && messagesResponse?.data?.messages) {
          setMessages(messagesResponse.data.messages);
        }
      } else {
        setError(response.message || 'Failed to send message');
      }
    } catch {
      setError('Failed to send message');
    }
  };

  // Handle textarea auto-resize
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 115) + 'px';
  };

  // Handle Enter to send
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e as any);
    }
  };

  // Logout
  const handleLogout = async () => {
    setShowMenu(false);
    try {
      await Promise.race([
        apiService.logout(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000))
      ]);
    } catch { /* continue */ } finally {
      clearAuthData();
      navigate('/login');
    }
  };

  // Create new contact
  const handleCreateContact = async () => {
    if (!newContactForm.phoneNumber) return;
    try {
      const response = await apiService.createContact({
        phoneNumber: newContactForm.phoneNumber,
        displayName: newContactForm.displayName || undefined,
        firstName: newContactForm.firstName || undefined,
        lastName: newContactForm.lastName || undefined,
      });
      if (response.success) {
        setShowNewContactModal(false);
        setNewContactForm({ phoneNumber: '', displayName: '', firstName: '', lastName: '' });
        // Reload contacts
        const contactsResponse = await apiService.getContacts();
        if (contactsResponse?.success && contactsResponse?.data) {
          setContacts(Array.isArray(contactsResponse.data) ? contactsResponse.data : []);
        }
      } else {
        setError(response.message || 'Failed to create contact');
      }
    } catch {
      setError('Failed to create contact');
    }
  };

  // Open edit contact modal
  const openEditContact = (contact: Contact | null, phoneNumber: string) => {
    setEditContactForm({
      contactId: contact?.contactId || 0,
      phoneNumber: phoneNumber,
      displayName: contact?.displayName || '',
      firstName: contact?.firstName || '',
      lastName: contact?.lastName || '',
      email: contact?.email || '',
    });
    setShowEditContactModal(true);
  };

  // Update contact
  const handleUpdateContact = async () => {
    try {
      if (editContactForm.contactId) {
        // Update existing contact
        const response = await apiService.updateContact(editContactForm.contactId, {
          displayName: editContactForm.displayName || undefined,
          firstName: editContactForm.firstName || undefined,
          lastName: editContactForm.lastName || undefined,
          email: editContactForm.email || undefined,
        });
        if (!response.success) {
          setError(response.message || 'Failed to update contact');
          return;
        }
      } else {
        // Create new contact (for phone numbers without a contact entry)
        const response = await apiService.createContact({
          phoneNumber: editContactForm.phoneNumber,
          displayName: editContactForm.displayName || undefined,
          firstName: editContactForm.firstName || undefined,
          lastName: editContactForm.lastName || undefined,
        });
        if (!response.success) {
          setError(response.message || 'Failed to create contact');
          return;
        }
      }
      setShowEditContactModal(false);
      // Reload contacts
      const contactsResponse = await apiService.getContacts();
      if (contactsResponse?.success && contactsResponse?.data) {
        setContacts(Array.isArray(contactsResponse.data) ? contactsResponse.data : []);
      }
    } catch {
      setError('Failed to update contact');
    }
  };

  // Delete single message
  const handleDeleteMessage = async () => {
    if (!deleteConfirm || deleting) return;
    setDeleting(true);
    try {
      const response = await apiService.deleteMessages([deleteConfirm.messageId]);
      if (response.success) {
        setChatMessages(prev => prev.filter(m => m.messageId !== deleteConfirm.messageId));
        const messagesResponse = await apiService.getMessages(1, 100);
        if (messagesResponse?.success && messagesResponse?.data?.messages) {
          setMessages(messagesResponse.data.messages);
        }
      } else {
        setError(response.message || 'Failed to delete message');
      }
    } catch {
      setError('Failed to delete message');
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
      setMessageMenuId(null);
    }
  };

  // Bulk delete messages
  const handleBulkDelete = async () => {
    if (selectedMessageIds.size === 0 || deleting) return;
    setDeleting(true);
    try {
      const ids = Array.from(selectedMessageIds);
      const response = await apiService.deleteMessages(ids);
      if (response.success) {
        setChatMessages(prev => prev.filter(m => !selectedMessageIds.has(m.messageId)));
        const messagesResponse = await apiService.getMessages(1, 100);
        if (messagesResponse?.success && messagesResponse?.data?.messages) {
          setMessages(messagesResponse.data.messages);
        }
      } else {
        setError(response.message || 'Failed to delete messages');
      }
    } catch {
      setError('Failed to delete messages');
    } finally {
      setDeleting(false);
      setBulkDeleteConfirm(false);
      setSelectedMessageIds(new Set());
      setBulkSelectMode(false);
    }
  };

  // Toggle message selection for bulk delete
  const toggleMessageSelection = (messageId: string) => {
    setSelectedMessageIds(prev => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  // Exit bulk select mode
  const exitBulkSelect = () => {
    setBulkSelectMode(false);
    setSelectedMessageIds(new Set());
  };

  // Start chat from new chat panel
  const startChat = (phoneNumber: string) => {
    setShowNewChat(false);
    setNewContactSearch('');
    selectChat(phoneNumber);
  };

  // Helpers
  const getConversationName = (conv: Conversation) => {
    if (conv.contact) {
      return conv.contact.displayName ||
        `${conv.contact.firstName || ''} ${conv.contact.lastName || ''}`.trim() ||
        conv.phoneNumber || 'Unknown';
    }
    return conv.phoneNumber || 'Unknown';
  };

  const getContactName = (contact: Contact) => {
    return contact.displayName ||
      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
      contact.phoneNumber || 'Unknown';
  };

  const getInitials = (name: string) => {
    if (!name || name.startsWith('+') || /^\d+$/.test(name)) return '';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.[0] || '').toUpperCase();
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#00A884', '#53BDEB', '#FFA726', '#E57373', '#AB47BC', '#7986CB', '#4DB6AC', '#FF8A65'];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  // All time formatting uses explicit IST timezone to avoid browser timezone issues
  const IST_TZ = 'Asia/Kolkata';

  const formatChatTime = (dateString: string) => {
    const date = parseDate(dateString);
    const now = new Date();
    // Compare dates in IST
    const istDateStr = date.toLocaleDateString('en-CA', { timeZone: IST_TZ }); // YYYY-MM-DD
    const istTodayStr = now.toLocaleDateString('en-CA', { timeZone: IST_TZ });
    const yesterday = new Date(now.getTime() - 86400000);
    const istYesterdayStr = yesterday.toLocaleDateString('en-CA', { timeZone: IST_TZ });

    if (istDateStr === istTodayStr) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: IST_TZ });
    }
    if (istDateStr === istYesterdayStr) return 'Yesterday';
    if (now.getTime() - date.getTime() < 7 * 86400000) {
      return date.toLocaleDateString('en-US', { weekday: 'short', timeZone: IST_TZ });
    }
    return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: IST_TZ });
  };

  // Convert WhatsApp-style formatting to HTML: *bold*, _italic_, ~strikethrough~, `code`
  const formatWhatsAppText = (text: string): string => {
    let formatted = text
      // Escape HTML entities first
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Apply WhatsApp formatting (order matters: bold before italic to avoid conflicts)
    formatted = formatted
      .replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>')
      .replace(/_([^_\n]+)_/g, '<em>$1</em>')
      .replace(/~([^~\n]+)~/g, '<del>$1</del>')
      .replace(/`([^`\n]+)`/g, '<code>$1</code>');
    return formatted;
  };

  const formatMessageTime = (dateString: string) => {
    const date = parseDate(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: IST_TZ });
  };

  const formatDateDivider = (dateString: string) => {
    const date = parseDate(dateString);
    const now = new Date();
    // Compare dates in IST
    const istDateStr = date.toLocaleDateString('en-CA', { timeZone: IST_TZ });
    const istTodayStr = now.toLocaleDateString('en-CA', { timeZone: IST_TZ });
    const yesterday = new Date(now.getTime() - 86400000);
    const istYesterdayStr = yesterday.toLocaleDateString('en-CA', { timeZone: IST_TZ });

    if (istDateStr === istTodayStr) return 'TODAY';
    if (istDateStr === istYesterdayStr) return 'YESTERDAY';
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: IST_TZ }).toUpperCase();
  };

  const renderTickIcon = (status: string) => {
    switch (status) {
      case 'read':
        return <span className="wa-message-tick read"><DoubleTickIcon /></span>;
      case 'delivered':
        return <span className="wa-message-tick delivered"><DoubleTickIcon /></span>;
      case 'sent':
        return <span className="wa-message-tick sent"><SingleTickIcon /></span>;
      case 'failed':
        return <span className="wa-message-tick failed"><FailedIcon /></span>;
      case 'pending':
        return <span className="wa-message-tick pending"><SingleTickIcon /></span>;
      default:
        return null;
    }
  };

  const renderPreviewTick = (status: string) => {
    switch (status) {
      case 'read':
        return <span className="wa-tick read"><DoubleTickIcon /></span>;
      case 'delivered':
        return <span className="wa-tick delivered"><DoubleTickIcon /></span>;
      case 'sent':
        return <span className="wa-tick sent"><SingleTickIcon /></span>;
      case 'failed':
        return <span className="wa-tick failed"><FailedIcon /></span>;
      default:
        return null;
    }
  };

  // Get selected conversation - also check contacts list for message-less contacts
  const selectedConversation = selectedPhone
    ? getConversations().find(c => c.phoneNumber === selectedPhone)
      || (() => {
        const contact = contacts.find(c => c.phoneNumber === selectedPhone) || null;
        return { phoneNumber: selectedPhone, contact, lastMessage: null, messages: [] } as Conversation;
      })()
    : null;

  // Group messages by date (using IST date)
  const groupMessagesByDate = (msgs: Message[]) => {
    const groups: { date: string; messages: Message[] }[] = [];
    let currentDate = '';

    msgs.forEach(msg => {
      const msgDate = parseDate(msg.createdAt);
      const dateKey = msgDate.toLocaleDateString('en-CA', { timeZone: IST_TZ }); // YYYY-MM-DD in IST
      if (dateKey !== currentDate) {
        currentDate = dateKey;
        groups.push({ date: msg.createdAt, messages: [msg] });
      } else {
        groups[groups.length - 1].messages.push(msg);
      }
    });

    return groups;
  };

  // Loading screen
  if (loading) {
    return (
      <div className="wa-loading">
        <div className="wa-loading-spinner"></div>
        <p>WhatsApp</p>
      </div>
    );
  }

  const conversations = filteredConversations();
  const messageGroups = groupMessagesByDate(chatMessages);

  return (
    <div className="whatsapp-app">
      {/* ==================== LEFT SIDEBAR ==================== */}
      <div className={`wa-sidebar ${mobileShowChat ? 'hidden' : ''}`}>

        {/* Sidebar Header */}
        <div className="wa-sidebar-header">
          <div className="wa-sidebar-header-left">
            <div className="wa-user-avatar" style={{ backgroundColor: user ? getAvatarColor(user.firstName || user.username) : undefined }}>
              {getInitials(user?.firstName || user?.username || '') || '?'}
            </div>
          </div>
          <div className="wa-sidebar-header-right">
            <button className="wa-icon-btn" title="New chat" onClick={() => setShowNewChat(true)}>
              <NewChatIcon />
            </button>
            <div ref={menuRef} style={{ position: 'relative' }}>
              <button className="wa-icon-btn" title="Menu" onClick={() => setShowMenu(!showMenu)}>
                <MenuIcon />
              </button>
              {showMenu && (
                <div className="wa-dropdown">
                  <button className="wa-dropdown-item" onClick={() => { setShowNewChat(true); setShowMenu(false); }}>New chat</button>
                  <button className="wa-dropdown-item" onClick={() => { setShowNewContactModal(true); setShowMenu(false); }}>New contact</button>
                  <button className="wa-dropdown-item danger" onClick={handleLogout}>Log out</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="wa-search-box">
          <div className="wa-search-container">
            <SearchIcon />
            <input
              type="text"
              placeholder="Search or start a new chat"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="wa-filter-tabs">
          <button className={`wa-filter-tab ${filterTab === 'all' ? 'active' : ''}`} onClick={() => setFilterTab('all')}>All</button>
          <button className={`wa-filter-tab ${filterTab === 'unread' ? 'active' : ''}`} onClick={() => setFilterTab('unread')}>Unread</button>
          <button className={`wa-filter-tab ${filterTab === 'contacts' ? 'active' : ''}`} onClick={() => setFilterTab('contacts')}>Contacts</button>
        </div>

        {/* Chat List */}
        <div className="wa-chat-list">
          {conversations.length === 0 ? (
            <div className="wa-empty-chats">
              <p>{filterTab === 'contacts' ? 'No contacts yet' : filterTab === 'unread' ? 'No unread messages' : 'No conversations yet'}</p>
              <p style={{ fontSize: 13, marginTop: 4 }}>{filterTab === 'contacts' ? 'Add a new contact to get started' : 'Start a new chat to begin messaging'}</p>
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.phoneNumber}
                className={`wa-chat-item ${selectedPhone === conv.phoneNumber ? 'active' : ''}`}
                onClick={() => selectChat(conv.phoneNumber)}
              >
                <div className="wa-chat-item-inner">
                  <div className="wa-contact-avatar" style={{ backgroundColor: getAvatarColor(getConversationName(conv)) }}>
                    {getInitials(getConversationName(conv)) || (
                      <svg viewBox="0 0 212 212" width="49" height="49"><path fill="#DFE5E7" d="M106.251.5C164.653.5 212 47.846 212 106.25S164.653 212 106.25 212C47.846 212 .5 164.654.5 106.25S47.846.5 106.251.5z"></path><path fill="#FFF" d="M173.561 171.615a62.767 62.767 0 0 0-2.065-2.955 67.7 67.7 0 0 0-2.608-3.299 70.112 70.112 0 0 0-3.184-3.527 71.097 71.097 0 0 0-5.924-5.47 72.458 72.458 0 0 0-10.204-7.026 75.2 75.2 0 0 0-5.98-3.055c-.062-.028-.118-.059-.18-.087-9.792-4.44-22.106-7.529-37.416-7.529s-27.624 3.089-37.416 7.529c-.338.153-.653.318-.985.474a75.37 75.37 0 0 0-6.229 3.298 72.589 72.589 0 0 0-9.15 6.395 71.243 71.243 0 0 0-5.924 5.47 70.064 70.064 0 0 0-3.184 3.527 67.142 67.142 0 0 0-2.609 3.299 63.292 63.292 0 0 0-2.065 2.955 56.33 56.33 0 0 0-1.447 2.324c-.033.056-.073.119-.104.174a47.92 47.92 0 0 0-1.07 1.926c-.559 1.068-.818 1.678-.818 1.678v.398c18.285 17.927 43.322 28.985 70.945 28.985 27.678 0 52.761-11.103 71.055-29.095v-.289s-.619-1.45-1.992-3.778a58.346 58.346 0 0 0-1.446-2.322zM106.002 125.5c2.645 0 5.212-.253 7.68-.737a38.272 38.272 0 0 0 3.624-.896 37.124 37.124 0 0 0 5.12-1.958 36.307 36.307 0 0 0 6.15-3.67 35.923 35.923 0 0 0 9.489-10.48 36.558 36.558 0 0 0 2.422-4.84 37.051 37.051 0 0 0 1.716-5.25c.299-1.208.542-2.443.725-3.701.275-1.887.417-3.827.417-5.811s-.142-3.925-.417-5.811a38.734 38.734 0 0 0-.725-3.701 37.165 37.165 0 0 0-1.716-5.25 36.612 36.612 0 0 0-2.422-4.84 35.923 35.923 0 0 0-9.489-10.48 36.347 36.347 0 0 0-6.15-3.67 37.124 37.124 0 0 0-5.12-1.958 37.67 37.67 0 0 0-3.624-.896 39.875 39.875 0 0 0-7.68-.737c-21.162 0-37.345 16.183-37.345 37.345 0 21.159 16.183 37.342 37.345 37.342z"></path></svg>
                    )}
                  </div>
                  <div className="wa-chat-item-content">
                    <div className="wa-chat-item-top">
                      <span className="wa-chat-item-name">{getConversationName(conv)}</span>
                      {conv.lastMessage && (
                        <span className={`wa-chat-item-time ${conv.lastMessage.status === 'failed' ? 'failed' : ''}`}>
                          {formatChatTime(conv.lastMessage.createdAt)}
                        </span>
                      )}
                    </div>
                    {conv.contact?.displayName && (
                      <div className="wa-chat-item-phone">{conv.phoneNumber}</div>
                    )}
                    <div className="wa-chat-item-bottom">
                      <span className="wa-chat-item-preview">
                        {conv.lastMessage ? (
                          <>
                            {conv.lastMessage.messageType === 'outgoing' && renderPreviewTick(conv.lastMessage.status)}
                            {conv.lastMessage.messageContent || (conv.lastMessage.messageType === 'media' ? 'Media' : '')}
                          </>
                        ) : (
                          'No messages yet'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* New Chat Panel (slides over sidebar) */}
        {showNewChat && (
          <div className="wa-new-chat-panel">
            <div className="wa-new-chat-header">
              <button className="wa-back-btn" onClick={() => { setShowNewChat(false); setNewContactSearch(''); }}>
                <BackIcon />
              </button>
              <h3>New chat</h3>
            </div>
            <div className="wa-new-chat-search">
              <div className="wa-search-container">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Search contacts"
                  value={newContactSearch}
                  onChange={(e) => setNewContactSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="wa-new-contact-btn-row">
              <div className="wa-new-contact-item" onClick={() => { setShowNewContactModal(true); }}>
                <div className="wa-new-contact-icon">
                  <PersonAddIcon />
                </div>
                <span>New contact</span>
              </div>
            </div>
            <div className="wa-contacts-section-label">Contacts on WhatsApp</div>
            <div className="wa-chat-list">
              {contacts
                .filter(c => {
                  if (!newContactSearch) return true;
                  const q = newContactSearch.toLowerCase();
                  return getContactName(c).toLowerCase().includes(q) || c.phoneNumber.includes(q);
                })
                .map(contact => (
                  <div
                    key={contact.contactId}
                    className="wa-contact-list-item"
                    onClick={() => startChat(contact.phoneNumber)}
                  >
                    <div className="wa-contact-avatar" style={{ backgroundColor: getAvatarColor(getContactName(contact)) }}>
                      {getInitials(getContactName(contact)) || (
                        <svg viewBox="0 0 212 212" width="49" height="49"><path fill="#DFE5E7" d="M106.251.5C164.653.5 212 47.846 212 106.25S164.653 212 106.25 212C47.846 212 .5 164.654.5 106.25S47.846.5 106.251.5z"></path><path fill="#FFF" d="M173.561 171.615a62.767 62.767 0 0 0-2.065-2.955 67.7 67.7 0 0 0-2.608-3.299 70.112 70.112 0 0 0-3.184-3.527 71.097 71.097 0 0 0-5.924-5.47 72.458 72.458 0 0 0-10.204-7.026 75.2 75.2 0 0 0-5.98-3.055c-.062-.028-.118-.059-.18-.087-9.792-4.44-22.106-7.529-37.416-7.529s-27.624 3.089-37.416 7.529c-.338.153-.653.318-.985.474a75.37 75.37 0 0 0-6.229 3.298 72.589 72.589 0 0 0-9.15 6.395 71.243 71.243 0 0 0-5.924 5.47 70.064 70.064 0 0 0-3.184 3.527 67.142 67.142 0 0 0-2.609 3.299 63.292 63.292 0 0 0-2.065 2.955 56.33 56.33 0 0 0-1.447 2.324c-.033.056-.073.119-.104.174a47.92 47.92 0 0 0-1.07 1.926c-.559 1.068-.818 1.678-.818 1.678v.398c18.285 17.927 43.322 28.985 70.945 28.985 27.678 0 52.761-11.103 71.055-29.095v-.289s-.619-1.45-1.992-3.778a58.346 58.346 0 0 0-1.446-2.322zM106.002 125.5c2.645 0 5.212-.253 7.68-.737a38.272 38.272 0 0 0 3.624-.896 37.124 37.124 0 0 0 5.12-1.958 36.307 36.307 0 0 0 6.15-3.67 35.923 35.923 0 0 0 9.489-10.48 36.558 36.558 0 0 0 2.422-4.84 37.051 37.051 0 0 0 1.716-5.25c.299-1.208.542-2.443.725-3.701.275-1.887.417-3.827.417-5.811s-.142-3.925-.417-5.811a38.734 38.734 0 0 0-.725-3.701 37.165 37.165 0 0 0-1.716-5.25 36.612 36.612 0 0 0-2.422-4.84 35.923 35.923 0 0 0-9.489-10.48 36.347 36.347 0 0 0-6.15-3.67 37.124 37.124 0 0 0-5.12-1.958 37.67 37.67 0 0 0-3.624-.896 39.875 39.875 0 0 0-7.68-.737c-21.162 0-37.345 16.183-37.345 37.345 0 21.159 16.183 37.342 37.345 37.342z"></path></svg>
                      )}
                    </div>
                    <div className="wa-contact-list-info">
                      <span className="wa-contact-list-name">{getContactName(contact)}</span>
                      <span className="wa-contact-list-phone">{contact.phoneNumber}</span>
                    </div>
                  </div>
                ))}
              {contacts.length === 0 && (
                <div className="wa-empty-chats">
                  <p>No contacts found</p>
                  <p style={{ fontSize: 13, marginTop: 4 }}>Add a new contact to start chatting</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ==================== RIGHT PANEL - CHAT ==================== */}
      <div className={`wa-chat-panel ${mobileShowChat ? 'visible' : ''} ${selectedPhone ? 'active' : ''}`}>
        {!selectedPhone ? (
          /* Welcome Screen */
          <div className="wa-intro-screen">
            <div className="wa-intro-icon">
              <svg viewBox="0 0 303 172" width="303" height="172">
                <path fill="currentColor" d="M229.565 160.229c32.47-42.327 24.17-102.884-18.158-135.354C169.08-7.596 108.522.704 76.053 43.03c-30.418 39.693-24.915 94.905 11.157 128.453l-.725.755c-.934.975-.14 2.531 1.192 2.338l19.336-2.798a97.504 97.504 0 0 0 33.454 8.463l.052-.06c1.644.127 3.3.205 4.968.231 1.669.027 3.332.004 4.99-.067l.058.066a97.479 97.479 0 0 0 34.296-5.136l18.635 6.57c1.279.452 2.457-.801 1.77-1.882l-7.085-11.149a97.479 97.479 0 0 0 30.414-28.585z"></path>
                <path fill="var(--wa-intro-bg)" d="M187.481 131.21c-24.94 32.538-72.237 38.856-105.56 14.113-33.323-24.743-39.7-71.854-14.76-104.392 24.94-32.538 72.237-38.856 105.56-14.113s39.7 71.854 14.76 104.392z"></path>
                <path fill="currentColor" d="M134.579 96.006l-24.168-6.755-2.027 7.246 24.168 6.756zm-11.534-13.39l-13.494-3.773-2.027 7.247 13.494 3.772zm26.098 7.293l-13.494-3.772-2.027 7.246 13.494 3.773zm14.959 23.39l-24.168-6.756-2.027 7.246 24.168 6.756zm-64.59-33.63l-7.129-1.993-2.027 7.246 7.128 1.993zm82.92 23.184l-7.128-1.993-2.028 7.246 7.129 1.993z"></path>
              </svg>
            </div>
            <h2>WhatsApp Web</h2>
            <p>Send and receive messages without keeping your phone online.</p>
            <p>Use WhatsApp on up to 4 linked devices and 1 phone at the same time.</p>
            <hr className="wa-intro-divider" />
            <div className="wa-intro-encryption">
              <LockIcon />
              <span>End-to-end encrypted</span>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header - normal or bulk select mode */}
            {bulkSelectMode ? (
              <div className="wa-chat-header wa-bulk-header">
                <div className="wa-chat-header-left">
                  <button className="wa-icon-btn" onClick={exitBulkSelect}>
                    <CloseIcon />
                  </button>
                  <span className="wa-bulk-count">{selectedMessageIds.size} selected</span>
                </div>
                <div className="wa-chat-header-right">
                  <button
                    className="wa-icon-btn wa-bulk-delete-btn"
                    title="Delete selected"
                    disabled={selectedMessageIds.size === 0}
                    onClick={() => setBulkDeleteConfirm(true)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ) : (
              <div className="wa-chat-header">
                <div className="wa-chat-header-left">
                  <button className="wa-mobile-back" onClick={() => { setMobileShowChat(false); setSelectedPhone(null); }}>
                    <BackIcon />
                  </button>
                  <div
                    className="wa-chat-header-avatar"
                    style={{ backgroundColor: getAvatarColor(selectedConversation ? getConversationName(selectedConversation) : selectedPhone) }}
                  >
                    {getInitials(selectedConversation ? getConversationName(selectedConversation) : '') || '?'}
                  </div>
                  <div className="wa-chat-header-info">
                    <span className="wa-chat-header-name">
                      {selectedConversation ? getConversationName(selectedConversation) : selectedPhone}
                    </span>
                    <span className="wa-chat-header-status">
                      {selectedPhone}
                    </span>
                  </div>
                </div>
                <div className="wa-chat-header-right">
                  <button
                    className="wa-icon-btn"
                    title="Edit contact"
                    onClick={() => {
                      const conv = getConversations().find(c => c.phoneNumber === selectedPhone);
                      openEditContact(conv?.contact || null, selectedPhone!);
                    }}
                  >
                    <EditIcon />
                  </button>
                  <button className="wa-icon-btn" title="Search">
                    <SearchIcon />
                  </button>
                </div>
              </div>
            )}

            {/* Error Banner */}
            {error && (
              <div className="wa-error-banner">
                <span>{error}</span>
                <button onClick={() => setError('')}>&times;</button>
              </div>
            )}

            {/* Messages Area */}
            <div className="wa-messages-area" ref={messagesAreaRef}>
              {chatLoading ? (
                <div className="wa-no-messages">
                  <div className="wa-loading-spinner" style={{ width: 32, height: 32 }}></div>
                </div>
              ) : chatMessages.length === 0 ? (
                <div className="wa-no-messages">
                  <p>No messages yet. Say hello!</p>
                </div>
              ) : (
                <>
                {/* Load More button - shown when there are older messages */}
                {chatMessages.length < chatTotal && (
                  <div className="wa-load-more">
                    <button
                      className="wa-load-more-btn"
                      onClick={loadMoreMessages}
                      disabled={loadingMore}
                    >
                      {loadingMore ? 'Loading...' : `Load older messages (${chatTotal - chatMessages.length} more)`}
                    </button>
                  </div>
                )}
                {messageGroups.map((group, gi) => (
                  <React.Fragment key={gi}>
                    <div className="wa-date-divider">
                      <span className="wa-date-label">{formatDateDivider(group.date)}</span>
                    </div>
                    {group.messages.map((msg, mi) => {
                      const direction = msg.messageType === 'outgoing' ? 'outgoing' : 'incoming';
                      const prevMsg = mi > 0 ? group.messages[mi - 1] : null;
                      const prevDirection = prevMsg ? (prevMsg.messageType === 'outgoing' ? 'outgoing' : 'incoming') : null;
                      const isFirstInGroup = direction !== prevDirection;
                      const hasMedia = msg.mediaFiles && Array.isArray(msg.mediaFiles) && msg.mediaFiles.length > 0;

                      return (
                        <div
                          key={msg.messageId}
                          className={`wa-message-row ${direction} ${isFirstInGroup ? 'first-in-group' : ''} ${msg.status === 'failed' ? 'failed' : ''} ${bulkSelectMode ? 'bulk-mode' : ''} ${selectedMessageIds.has(msg.messageId) ? 'selected' : ''}`}
                          onClick={bulkSelectMode ? () => toggleMessageSelection(msg.messageId) : undefined}
                        >
                          {/* Bulk select checkbox */}
                          {bulkSelectMode && (
                            <div className="wa-bulk-checkbox">
                              <input
                                type="checkbox"
                                checked={selectedMessageIds.has(msg.messageId)}
                                onChange={() => toggleMessageSelection(msg.messageId)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          )}
                          <div className="wa-message-bubble">
                            {/* Message dropdown menu */}
                            {!bulkSelectMode && (
                              <div className="wa-msg-menu-container">
                                <button
                                  className="wa-msg-menu-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMessageMenuId(messageMenuId === msg.messageId ? null : msg.messageId);
                                  }}
                                >
                                  <ChevronDownIcon />
                                </button>
                                {messageMenuId === msg.messageId && (
                                  <div className={`wa-msg-dropdown ${direction}`}>
                                    <button
                                      className="wa-msg-dropdown-item danger"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteConfirm({
                                          messageId: msg.messageId,
                                          content: msg.messageContent?.substring(0, 80) || (msg.mediaFiles?.length ? 'Media message' : 'Message'),
                                        });
                                        setMessageMenuId(null);
                                      }}
                                    >
                                      <TrashIcon />
                                      <span>Delete</span>
                                    </button>
                                    <button
                                      className="wa-msg-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMessageMenuId(null);
                                        setBulkSelectMode(true);
                                        setSelectedMessageIds(new Set([msg.messageId]));
                                      }}
                                    >
                                      <span>Select messages</span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            {/* Media */}
                            {hasMedia && msg.mediaFiles!.map((media, idx) => (
                              <div key={idx} className="wa-message-media">
                                {media.fileType === 'image' ? (
                                  <img
                                    src={media.url}
                                    alt={media.filename}
                                    onClick={() => setViewerImage({ url: media.url, filename: media.filename })}
                                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                  />
                                ) : (
                                  <div
                                    className="wa-media-file"
                                    onClick={() => {
                                      const link = document.createElement('a');
                                      link.href = media.url;
                                      link.download = media.filename;
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <div className="wa-media-file-icon">
                                      {media.fileType === 'video' && '\uD83C\uDFA5'}
                                      {media.fileType === 'audio' && '\uD83C\uDFB5'}
                                      {media.fileType === 'document' && '\uD83D\uDCC4'}
                                    </div>
                                    <div className="wa-media-file-info">
                                      <span className="wa-media-file-name">{media.filename}</span>
                                      <span className="wa-media-file-type">{media.mimeType}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                            {/* Text */}
                            {msg.messageContent && (
                              <span className="wa-message-text" dangerouslySetInnerHTML={{ __html: formatWhatsAppText(msg.messageContent) }} />
                            )}
                            {/* Meta */}
                            <span className="wa-message-meta">
                              <span className="wa-message-timestamp">{formatMessageTime(msg.createdAt)}</span>
                              {direction === 'outgoing' && renderTickIcon(msg.status)}
                            </span>
                            {/* Error message for failed delivery */}
                            {msg.status === 'failed' && msg.errorMessage && (
                              <div className="wa-message-error">
                                {msg.errorMessage}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="wa-message-input-area">
              <div className="wa-input-wrapper">
                <textarea
                  ref={textareaRef}
                  className="wa-message-textarea"
                  placeholder="Type a message"
                  value={newMessage}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  rows={1}
                />
              </div>
              <button
                className={`wa-send-btn ${newMessage.trim() ? 'has-text' : ''}`}
                onClick={handleSendMessage as any}
                title="Send"
              >
                <SendIcon />
              </button>
            </div>
          </>
        )}
      </div>

      {/* ==================== IMAGE VIEWER LIGHTBOX ==================== */}
      {viewerImage && (
        <div className="wa-image-viewer-overlay" onClick={() => setViewerImage(null)}>
          <div className="wa-image-viewer-header">
            <button className="wa-image-viewer-btn" onClick={() => setViewerImage(null)}>
              <CloseIcon />
            </button>
            <button
              className="wa-image-viewer-btn"
              onClick={(e) => {
                e.stopPropagation();
                const link = document.createElement('a');
                link.href = viewerImage.url;
                link.download = viewerImage.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              <DownloadIcon />
            </button>
          </div>
          <div className="wa-image-viewer-body" onClick={(e) => e.stopPropagation()}>
            <img src={viewerImage.url} alt={viewerImage.filename} />
          </div>
        </div>
      )}

      {/* ==================== NEW CONTACT MODAL ==================== */}
      {showNewContactModal && (
        <div className="wa-modal-overlay" onClick={() => setShowNewContactModal(false)}>
          <div className="wa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-modal-header">
              <button className="wa-modal-close" onClick={() => setShowNewContactModal(false)}>
                <CloseIcon />
              </button>
              <h3>New contact</h3>
            </div>
            <div className="wa-modal-body">
              <div className="wa-form-group">
                <label>Phone number</label>
                <input
                  type="text"
                  placeholder="+1 234 567 8900"
                  value={newContactForm.phoneNumber}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="wa-form-group">
                <label>Display name</label>
                <input
                  type="text"
                  placeholder="Name"
                  value={newContactForm.displayName}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, displayName: e.target.value }))}
                />
              </div>
              <div className="wa-form-group">
                <label>First name</label>
                <input
                  type="text"
                  placeholder="First name"
                  value={newContactForm.firstName}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </div>
              <div className="wa-form-group">
                <label>Last name</label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={newContactForm.lastName}
                  onChange={(e) => setNewContactForm(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </div>
            </div>
            <div className="wa-modal-footer">
              <button className="wa-btn-cancel" onClick={() => setShowNewContactModal(false)}>Cancel</button>
              <button
                className="wa-btn-primary"
                onClick={handleCreateContact}
                disabled={!newContactForm.phoneNumber}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== EDIT CONTACT MODAL ==================== */}
      {showEditContactModal && (
        <div className="wa-modal-overlay" onClick={() => setShowEditContactModal(false)}>
          <div className="wa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-modal-header">
              <button className="wa-modal-close" onClick={() => setShowEditContactModal(false)}>
                <CloseIcon />
              </button>
              <h3>{editContactForm.contactId ? 'Edit contact' : 'Add contact'}</h3>
            </div>
            <div className="wa-modal-body">
              <div className="wa-form-group">
                <label>Phone number</label>
                <input
                  type="text"
                  value={editContactForm.phoneNumber}
                  disabled
                  className="wa-input-disabled"
                />
              </div>
              <div className="wa-form-group">
                <label>Display name</label>
                <input
                  type="text"
                  placeholder="Display name"
                  value={editContactForm.displayName}
                  onChange={(e) => setEditContactForm(prev => ({ ...prev, displayName: e.target.value }))}
                  autoFocus
                />
              </div>
              <div className="wa-form-group">
                <label>First name</label>
                <input
                  type="text"
                  placeholder="First name"
                  value={editContactForm.firstName}
                  onChange={(e) => setEditContactForm(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </div>
              <div className="wa-form-group">
                <label>Last name</label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={editContactForm.lastName}
                  onChange={(e) => setEditContactForm(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </div>
              <div className="wa-form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={editContactForm.email}
                  onChange={(e) => setEditContactForm(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
            </div>
            <div className="wa-modal-footer">
              <button className="wa-btn-cancel" onClick={() => setShowEditContactModal(false)}>Cancel</button>
              <button
                className="wa-btn-primary"
                onClick={handleUpdateContact}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== DELETE CONFIRMATION MODAL (single) ==================== */}
      {deleteConfirm && (
        <div className="wa-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="wa-modal wa-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-modal-body">
              <p className="wa-delete-title">Delete message?</p>
              <p className="wa-delete-preview">"{deleteConfirm.content}{deleteConfirm.content.length >= 80 ? '...' : ''}"</p>
            </div>
            <div className="wa-modal-footer">
              <button className="wa-btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="wa-btn-danger" onClick={handleDeleteMessage} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== BULK DELETE CONFIRMATION MODAL ==================== */}
      {bulkDeleteConfirm && (
        <div className="wa-modal-overlay" onClick={() => setBulkDeleteConfirm(false)}>
          <div className="wa-modal wa-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="wa-modal-body">
              <p className="wa-delete-title">Delete {selectedMessageIds.size} message{selectedMessageIds.size > 1 ? 's' : ''}?</p>
              <p className="wa-delete-preview">This action cannot be undone. Associated media files will also be deleted.</p>
            </div>
            <div className="wa-modal-footer">
              <button className="wa-btn-cancel" onClick={() => setBulkDeleteConfirm(false)}>Cancel</button>
              <button className="wa-btn-danger" onClick={handleBulkDelete} disabled={deleting}>
                {deleting ? 'Deleting...' : `Delete ${selectedMessageIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
